import { type FunctionComponent, useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { FreeMode } from "swiper/modules";
import "swiper/css";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { FallbackReason, WidgetVersion } from "scmp-app/pages/home/ab-widget-fetch";
import type { seriesWidgetHomeV2Query$key } from "scmp-app/queries/__generated__/seriesWidgetHomeV2Query.graphql";

import { SeriesWidgetTabContent } from "./series-widget-tab-content";
import { Container, StyledSwiper, StyledSwiperSlide, Tab, TabsContainer, Title } from "./styles";

type Props = {
  className?: string;
  fallbackReason?: FallbackReason;
  reference: seriesWidgetHomeV2Query$key;
  widgetVersion?: WidgetVersion;
};

export const HomeV2SeriesWidget: FunctionComponent<Props> = ({
  className,
  fallbackReason,
  reference,
  widgetVersion,
}) => {
  const data = useFragment(
    graphql`
      fragment seriesWidgetHomeV2Query on Query
      @argumentDefinitions(seriesWidgetArticlesQueueName: { type: "String!" }) {
        seriesWidgetV2Articles: queue(filter: { name: $seriesWidgetArticlesQueueName }) {
          items {
            edges {
              node {
                ... on Article {
                  entityId
                  seriesShortLabel
                  series {
                    ... on Content {
                      entityId
                      publishedDate
                    }
                  }
                  ...seriesWidgetTabContentHomeV2Article
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const articles = useMemo(
    () =>
      data?.seriesWidgetV2Articles?.items?.edges?.filter(edge => edge.node.seriesShortLabel) ?? [],
    [data?.seriesWidgetV2Articles?.items?.edges],
  );
  const [tab, setTab] = useState(articles[0]?.node?.entityId);
  const [sentImpressions, setSentImpressions] = useState<Set<string>>(new Set());

  const selectedArticle = useMemo(
    () => articles.find(({ node }) => node.entityId === tab)?.node,
    [articles, tab],
  );
  const widgetTabOrder = useMemo(
    () => articles.findIndex(({ node }) => node.entityId === tab) + 1,
    [articles, tab],
  );
  const label = useMemo(
    () => selectedArticle?.seriesShortLabel?.replaceAll(" ", "_").toLowerCase() ?? "",
    [selectedArticle?.seriesShortLabel],
  );
  const listOfArticles = useMemo(
    () =>
      selectedArticle?.series
        ?.toSorted((a, b) => {
          const dateA = new Date(a?.publishedDate ?? 0).getTime();
          const dateB = new Date(b?.publishedDate ?? 0).getTime();
          return dateB - dateA;
        })
        .slice(0, 3)
        .map(s => s?.entityId)
        .filter(notEmpty)
        .join(",") ?? "",
    [selectedArticle?.series],
  );

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(() => {
    setSentImpressions(previous => new Set(previous).add(selectedArticle?.entityId ?? ""));
    return {
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: label,
        content_type: "series",
        list_of_articles: listOfArticles,
        page_section: "home",
        page_type: "homepage",
        result: fallbackReason ? "error" : "valid",
        widget_name: "scmp_series",
        widget_tab: widgetTabOrder,
        ...(fallbackReason && { result_error: fallbackReason }),
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    };
  }, [
    fallbackReason,
    label,
    listOfArticles,
    selectedArticle?.entityId,
    widgetTabOrder,
    widgetVersion,
  ]);

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: {
        isSendGA4Tracking: true,
        shouldDisable: !selectedArticle || sentImpressions.has(selectedArticle.entityId ?? ""),
        shouldSendOnce: false,
      },
    });

  if (!selectedArticle) return null;

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <Title>SCMP Series</Title>
      <TabsContainer>
        <StyledSwiper freeMode modules={[FreeMode]} slidesPerView="auto" spaceBetween={0}>
          {articles.map(({ node }) => (
            <StyledSwiperSlide key={node.entityId}>
              <Tab $isActive={tab === node.entityId} onClick={() => setTab(node.entityId)}>
                {node.seriesShortLabel}
              </Tab>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </TabsContainer>
      <SeriesWidgetTabContent
        fallbackReason={fallbackReason}
        listOfArticles={listOfArticles}
        order={widgetTabOrder}
        reference={selectedArticle}
        widgetVersion={widgetVersion}
      />
    </Container>
  );
};

HomeV2SeriesWidget.displayName = "HomeV2SeriesWidget";
